import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faEye, faStar, faLightbulb, faCog, faPenNib } from '@fortawesome/free-solid-svg-icons';

import { useTheme } from '../contexts/ThemeContext';
import '../@style/AboutPage.css';

const AboutPage = () => {
  const { isDarkMode } = useTheme();

  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      // Make hero elements visible immediately
      const heroElements = document.querySelectorAll('.about-hero .fade-in-up');
      heroElements.forEach(element => {
        element.classList.add('initial');
      });

      const observerOptions = {
        threshold: 0.05,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          } else {
            // Element viewport'tan çıktığında animasyon sınıfını kaldır
            entry.target.classList.remove('animate');
          }
        });
      }, observerOptions);

      // Observe all animated elements except hero elements
      const animatedElements = document.querySelectorAll('.fade-in-up:not(.about-hero .fade-in-up), .fade-in-left, .fade-in-right, .scale-in');
      
      animatedElements.forEach(element => {
        observer.observe(element);
      });

      return () => {
        animatedElements.forEach(element => {
          observer.unobserve(element);
        });
      };
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`about-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <h1 className="fade-in-up">Hakkımızda</h1>
          <p className="fade-in-up">ENG Kıymetli Madenler olarak müşterilerimize en iyi hizmeti sunmaya devam ediyoruz</p>
        </div>
      </section>

      {/* History Section */}
      <section className="history-section">
        <div className="container">
          <h2 className="fade-in-up">Tarihçe</h2>
          <div className="history-content fade-in-up">
            <p>
              ENG Kıymetli Madenler, kıymetli madenler sektöründe üç nesilden gelen bilgi birikimi, tecrübe ve ticari disiplinin bir yansıması olarak faaliyet göstermektedir. Köklü geçmişinden aldığı güçle, altın ve gümüş ticaretinde güven, süreklilik ve kalite odaklı bir yaklaşımı benimsemiştir.
            </p>
            <p>
              İstanbul ve Diyarbakır merkezli operasyonlarıyla, başta Güneydoğu Anadolu Bölgesi olmak üzere Türkiye genelinde kuyumculuk sektörüne ürün tedariği sağlayan ENG Kıymetli Madenler, güçlü tedarik zinciri ve gelişmiş lojistik altyapısı sayesinde ürünlerini geniş bir coğrafyaya hızlı ve güvenli şekilde ulaştırmaktadır.
            </p>
            <p>
              Gram altın, külçe altın, külçe gümüş ve sarrafiye ürün gruplarında, uluslararası standartlara uygun ve sertifikalı ürün portföyü ile faaliyet gösteren şirket; sektörde güvenilirliği, şeffaflığı ve rekabetçi fiyat politikasıyla öne çıkmaktadır.
            </p>
            <p>
              Üç kuşaktan aktarılan ticari anlayışı modern iş yönetimi ile birleştiren ENG Kıymetli Madenler, kıymetli madenler ticaretinde güçlü konumunu koruyarak sürdürülebilir büyümesini kararlılıkla devam ettirmektedir.
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values Section */}
      <section className="mission-vision-values">
        <div className="container">
          <div className="values-grid">
            <div className="value-card fade-in-up">
              <div className="value-icon mission">
                <FontAwesomeIcon icon={faRocket} />
              </div>
              <h3>Misyonumuz</h3>
              <p>
                Altın sektöründe güvenilirliği ve kaliteyi ön planda tutarak; müşterilerimize her zaman doğru, 
                hızlı ve şeffaf hizmet sunmayı, gelenekten gelen kuyumculuk kültürünü modern ticaret anlayışıyla 
                harmanlamayı misyon edindik.
              </p>
            </div>

            <div className="value-card fade-in-up">
              <div className="value-icon vision">
                <FontAwesomeIcon icon={faEye} />
              </div>
              <h3>Vizyonumuz</h3>
              <p>
                Sektördeki deneyimimiz ve yenilikçi yaklaşımımızla, Türkiye'de ve uluslararası pazarda tanınan, 
                müşterileriyle uzun vadeli güven ilişkileri kuran, dijitalleşmeye uyum sağlayan lider bir altın 
                markası olmayı hedefliyoruz.
              </p>
            </div>

            <div className="value-card fade-in-up">
              <div className="value-icon values">
                <FontAwesomeIcon icon={faStar} />
              </div>
              <h3>Değerlerimiz</h3>
              <p>
                Ticaretin temelinde güvenin yer aldığına inanırız; bu nedenle tüm süreçlerimizde şeffaflık, 
                kaliteli ürün, yıllara dayanan tecrübe, güçlü iş birlikleri ve değişen koşullara hızla adapte 
                olabilen dinamik bir yapı en büyük değerlerimizdir.
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* Trust Process Section */}
      <section className="trust-process">
        <div className="container">
          <h2 className="fade-in-up">Her Adımda Güven</h2>
          <p className="trust-description fade-in-up">
            Talep ile başlayan süreci, onay ve teslim aşamalarına kadar tamamen kontrolümüzde ve müşterimizin 
            bilgisi dahilinde yürütüyoruz. Güven bizim için ticaretin temelidir.
          </p>
          
          <div className="process-timeline">
            <div className="timeline-line"></div>
            
            <div className="process-step left">
              <div className="step-number scale-in">01</div>
              <div className="step-content fade-in-left">
                <h3>Neden ENG?</h3>
                <p>
                  Yıllara dayanan tecrübemiz, sürekli güncellenen fiyatlarımız, hazır stok gücümüz ve birebir 
                  iletişimde sağladığımız şeffaflık ile toptan altın ticaretinde tercih edilen güvenilir bir markayız.
                </p>
              </div>
              <div className="step-icon scale-in">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
            </div>

            <div className="process-step right">
              <div className="step-number scale-in">02</div>
              <div className="step-content fade-in-right">
                <h3>İşleyiş Modelimiz</h3>
                <p>
                  Hızlı talep, anlık fiyatlama, onaylı sipariş ve güçlü stoktan anında temin - ENG işleyişi, 
                  basit ama etkili adımlarla ilerler.
                </p>
              </div>
              <div className="step-icon scale-in">
                <FontAwesomeIcon icon={faCog} />
              </div>
            </div>

            <div className="process-step left">
              <div className="step-number scale-in">03</div>
              <div className="step-content fade-in-left">
                <h3>Güvenin Adımları</h3>
                <p>
                  Her işlemde şeffaf, hızlı ve çözüm odaklı bir yapı ile ilerliyoruz. Talebinizden ürün teslimine 
                  kadar tüm süreç bizim kontrolümüzde, sizin güvenliğinizle yürütülür.
                </p>
              </div>
              <div className="step-icon scale-in">
                <FontAwesomeIcon icon={faPenNib} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="mobile-app-section">
        <div className="container">
          <div className="app-content">
            <div className="app-text">
              <h2 className="fade-in-up">ENG Mobil Uygulaması</h2>
              <div className="app-features">
                <div className="app-feature fade-in-up">
                  <h4>Anlık Takip</h4>
                  <p>Mobil telefonunuzdan altın ve döviz fiyatlarını anlık takip edin, piyasa değişimlerini anında görün.</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>Sade ve Hızlı Arayüz</h4>
                  <p>Karmaşasız, sade tasarım ile fiyatları anında bulun, uygulamayı kolayca kullanın.</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>Favoriler</h4>
                  <p>Sık takip ettiğiniz ürünleri favorilere ekleyin, portföyünüz için kişiselleştirilmiş takip listesi oluşturun.</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>Çeviri Aracı</h4>
                  <p>Bir miktar girerek anlık kurlar üzerinden Türk Lirası, Dolar ve Euro karşılığını kolayca hesaplayın.</p>
                </div>
                <div className="app-feature fade-in-up">
                  <h4>Karanlık Mod Desteği</h4>
                  <p>Gece veya düşük ışık ortamlarında gözü yormayan karanlık mod seçeneği ile deneyiminizi kişiselleştirin.</p>
                </div>
              </div>
              <div className="app-download fade-in-up">
                <h3>Hemen İndirin</h3>
                <div className="download-buttons">
                  <a 
                    href="https://apps.apple.com/us/app/eng-kıymetli-madenler/id6751402030" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="download-btn app-store"
                  >
                    <img src={require('../assets/icons/appstore.png')} alt="App Store" />
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.aifasoft.eng&hl=tr" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="download-btn google-play"
                  >
                    <img src={require('../assets/icons/gplay.png')} alt="Google Play" />
                  </a>
                </div>
              </div>
            </div>
            <div className="app-mockups">
              <div className="phone-mockup light-theme fade-in-right">
                <img 
                  src={require('../assets/images/mob.jpg')} 
                  alt="ENG Mobil Uygulama - Açık Tema" 
                  className="mockup-image"
                />
              </div>
              <div className="phone-mockup dark-theme fade-in-right">
                <img 
                  src={require('../assets/images/mob2.jpg')} 
                  alt="ENG Mobil Uygulama - Koyu Tema" 
                  className="mockup-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
